import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Tooltip,
  Popper,
  Paper,
  ClickAwayListener
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material/styles';

const InfoAccordion: React.FC = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setOpen(!open);
  };

  const handleClickAway = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        <Tooltip title="System Information" placement="bottom">
          <IconButton
            onClick={handleClick}
            sx={{
              color: theme.palette.primary.contrastText,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 2,
              width: 32,
              height: 32,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderColor: 'rgba(255, 255, 255, 0.3)'
              },
              transition: 'all 0.2s ease-in-out'
            }}>
            <InfoIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>

        <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 1300 }}>
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              minWidth: 320,
              maxWidth: 400,
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              border: '1px solid rgba(0, 0, 0, 0.1)'
            }}>
            {/* Manifest Submission Section */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 }
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  minHeight: 48,
                  '&.Mui-expanded': { minHeight: 48 }
                }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  📋 Manifest Submission
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.5, mb: 1 }}>
                  <strong>When to submit:</strong>
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.5, mb: 1.5, pl: 1 }}>
                  • All SSCCs are staged and ready for transport
                  <br />
                  • End of shift or designated manifest times
                  <br />• Before courier pickup
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                  <strong>Note:</strong> Manifests can only be submitted when online.
                </Typography>
              </AccordionDetails>
            </Accordion>

            {/* Offline Mode Section */}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                '&:before': { display: 'none' },
                '&.Mui-expanded': { margin: 0 }
              }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  backgroundColor: 'rgba(255, 152, 0, 0.04)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                  minHeight: 48,
                  '&.Mui-expanded': { minHeight: 48 }
                }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  📶 Offline Mode
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 2, pb: 2 }}>
                <Typography variant="body2" sx={{ lineHeight: 1.5, mb: 1 }}>
                  <strong>What works offline:</strong>
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.5, mb: 1.5, pl: 1 }}>
                  • View existing data and SSCCs
                  <br />
                  • Search and browse records
                  <br />• Navigate between pages
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.5, mb: 1 }}>
                  <strong>Requires connection:</strong>
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.5, pl: 1 }}>
                  • Creating new records
                  <br />
                  • Printing labels
                  <br />• Submitting manifests
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default InfoAccordion;
